import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Drawer, Skeleton } from 'antd';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState } from '@share/utils';

import { FAILED_STATUSES, TERMINAL_STATUSES, useWalletCashOut } from '../../../wallet-cash-out/use-wallet-cash-out';
import { getHubThemeStyle } from '../../utils';
import { AccountEmptyState } from '../empty-state';

import { BrandLogo, StatusBadge } from './shared';
import styles from './styles.module.scss';

/**
 * Wallet → Cash-out history: every cash-out, newest first. Details (status, redemption link,
 * amounts, reference) open in a side drawer instead of replacing the page. Same data and the same
 * safe redemption redirect as the cash-out flow (`useWalletCashOut`).
 */
export const AccountCashOutHistory = (): React.ReactElement => {
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const {
    intl, isLoading, options, history, detail, isBusy, isRedirecting, formatPoints, formatMoney, openDetail,
    handleRedeem, handleStartOver,
  } = useWalletCashOut();
  const location = useLocation();
  const routerHistory = useHistory();

  // `?open=<code>` (from the cash-out page's recent list) opens that cash-out once the list is in.
  const openedFromLink = useRef(false);
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('open');
    if (!code || isLoading || openedFromLink.current) {
      return;
    }
    openedFromLink.current = true;
    if (history.some(cashOut => cashOut.cashOutCode === code)) {
      openDetail(code);
    }
  }, [location.search, isLoading, history, openDetail]);

  const closeDetail = () => {
    handleStartOver();
    if (location.search) {
      routerHistory.replace({ pathname: location.pathname });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.card}>
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      </div>
    );
  }

  const brandFor = (id?: string | null) => options?.brands.find(brand => brand.productId === id);
  const brandName = (id?: string | null) => brandFor(id)?.name ?? id ?? '';
  const dateTime = (value?: string | null) =>
    (value ? intl.formatDate(value, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

  if (!history.length) {
    return (
      <AccountEmptyState
        variant="panel"
        icon={faReceipt}
        title={intl.formatMessage({ id: 'my.account.hub.cashout.history.empty' })}
        body={intl.formatMessage({ id: 'my.account.hub.cashout.history.empty_body' })}
      />
    );
  }

  return (
    <div className={styles.cashOut}>
      {isRedirecting ? (
        <div className={styles.redirect} role="status" aria-live="polite">
          <div className={styles.redirectCard}>
            <span className={styles.spinner} aria-hidden="true" />
            <b>{intl.formatMessage({ id: 'wallet.cashout.redirect.title' })}</b>
            <span>{intl.formatMessage({ id: 'wallet.cashout.redirect.hint' })}</span>
          </div>
        </div>
      ) : null}

      <section className={styles.card} aria-label={intl.formatMessage({ id: 'my.account.hub.section.cashout_history' })}>
        <ul className={styles.history}>
          {history.map(cashOut => {
            const isOpen = detail?.cashOutCode === cashOut.cashOutCode;
            return (
              <li key={cashOut.cashOutCode} className={isOpen ? `${styles.historyRow} ${styles.historyRowOpen}` : styles.historyRow}>
                <BrandLogo brand={brandFor(cashOut.productId)} />
                <div className={styles.historyMain}>
                  <span className={styles.historyName}>{brandName(cashOut.productId)}</span>
                  <span className={styles.historyMeta}>
                    {intl.formatDate(cashOut.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' · '}
                    {intl.formatMessage({ id: 'wallet.cashout.history.debited' }, { points: formatPoints(cashOut.pointsDebited) })}
                  </span>
                </div>
                <div className={styles.historySide}>
                  <b>{formatMoney(cashOut.payoutAmount, cashOut.currencyCode)}</b>
                  <StatusBadge status={cashOut.status} />
                </div>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => openDetail(cashOut.cashOutCode)}
                  disabled={isBusy}
                >
                  {intl.formatMessage({ id: 'my.account.hub.cashout.history.view' })}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <Drawer
        open={!!detail}
        onClose={closeDetail}
        placement="right"
        // Full width on phones, 440px otherwise.
        width="min(440px, 100vw)"
        zIndex={4100}
        title={intl.formatMessage({ id: 'my.account.hub.cashout.history.details' })}
        className={styles.historyDrawer}
        style={getHubThemeStyle(account)}
        destroyOnClose
      >
        {detail ? (
          <div className={styles.historyDetail}>
            <div className={styles.historyDetailHead}>
              <BrandLogo brand={brandFor(detail.productId)} />
              <div className={styles.historyMain}>
                <span className={styles.historyName}>{brandName(detail.productId)}</span>
                <StatusBadge status={detail.status} />
              </div>
            </div>
            <span className={styles.resultAmount}>{formatMoney(detail.payoutAmount, detail.currencyCode)}</span>

            {detail.redemptionLink ? (
              <>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => handleRedeem(detail.redemptionLink as string)}
                >
                  {intl.formatMessage({ id: 'wallet.cashout.result.redeem' })}
                </button>
                <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.redeem.hint' })}</p>
              </>
            ) : null}
            {!TERMINAL_STATUSES.includes(detail.status) ? (
              <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.processing' })}</p>
            ) : null}
            {FAILED_STATUSES.includes(detail.status) ? (
              <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.failed' })}</p>
            ) : null}

            <dl className={styles.summaryRows}>
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.history.requested' })}</dt>
                <dd>{dateTime(detail.createdAt)}</dd>
              </div>
              {detail.completedAt ? (
                <div>
                  <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.history.completed' })}</dt>
                  <dd>{dateTime(detail.completedAt)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{intl.formatMessage({ id: 'wallet.cashout.confirm.debited' })}</dt>
                <dd>{formatPoints(detail.pointsDebited)}</dd>
              </div>
              <div>
                <dt>{intl.formatMessage({ id: 'wallet.cashout.confirm.fee' })}</dt>
                <dd>{formatMoney(detail.feeAmount, detail.currencyCode)}</dd>
              </div>
              {/* A failed cash-out issued nothing: the amount went back to the wallet. */}
              {FAILED_STATUSES.includes(detail.status) ? (
                <div>
                  <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.history.returned' })}</dt>
                  <dd>{formatPoints(detail.pointsDebited)}</dd>
                </div>
              ) : (
                <div>
                  <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.you_received' })}</dt>
                  <dd>{formatMoney(detail.payoutAmount, detail.currencyCode)}</dd>
                </div>
              )}
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.history.reference' })}</dt>
                <dd className={styles.reference}>{detail.cashOutCode}</dd>
              </div>
            </dl>

            {!TERMINAL_STATUSES.includes(detail.status) ? (
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={isBusy}
                onClick={() => openDetail(detail.cashOutCode)}
              >
                {intl.formatMessage({ id: 'wallet.cashout.result.refresh' })}
              </button>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};
